import { useState, useMemo } from "react";
import {
  canApprove,
  pendingRequests,
} from "../../utils/openHouseOpportunityHelpers";
import { formatTimeRange } from "../../utils/openHouseHelpers";
import {
  approveOpportunityRequest,
  declineAllOpportunityRequests,
  getDealById,
} from "../../utils/firebasefunctions";
import { sendSchedulingEmails } from "../../utils/openHouseSend";
import { notifyAgentOnOhDecision } from "../../utils/helper";
import { useStateValue } from "../../context/StateProvider";
import { toast } from "react-toastify";
import { useActionDialog } from "../ActionDialog";

export default function OpportunityApprovalQueue({ opportunities }) {
  const [{ user, emailTemplates }] = useStateValue();
  const { confirm, dialog } = useActionDialog();
  const [busyId, setBusyId] = useState("");

  // Opportunities still open, with pending requests, that this user may approve.
  const queue = useMemo(
    () =>
      (opportunities || []).filter(
        (o) =>
          o.status === "Open" &&
          pendingRequests(o.requests).length > 0 &&
          canApprove(o, user),
      ),
    [opportunities, user],
  );

  const handleApprove = async (opp, request) => {
    setBusyId(opp.id);
    try {
      const deal = await getDealById(opp.dealId);
      if (!deal) throw new Error("Listing not found");
      const { openHouseId, occupied } = await approveOpportunityRequest({
        opportunityId: opp.id,
        winnerRequestId: request.id,
        deal,
        userId: user.id,
      });
      // Vacant home → fire the scheduling emails now (mirrors ScheduleModal).
      // Occupied home → held for seller approval; released later from the list.
      if (!occupied) {
        sendSchedulingEmails(openHouseId, user, emailTemplates).catch((err) =>
          console.error("Scheduling email error:", err),
        );
      }
      // Notify the winner + the agents who weren't picked.
      notifyAgentOnOhDecision(opp, request, "approved").catch(() => {});
      pendingRequests(opp.requests)
        .filter((r) => r.id !== request.id)
        .forEach((r) => notifyAgentOnOhDecision(opp, r, "declined").catch(() => {}));
      toast.success(
        occupied
          ? "Approved — held for seller approval. Release it from the open-houses list."
          : "Approved and scheduled.",
      );
    } catch (err) {
      console.error("Approve failed:", err);
      toast.error(err.message || "Could not approve. Try again.");
    } finally {
      setBusyId("");
    }
  };

  const handleDeclineAll = async (opp) => {
    const ok = await confirm({
      title: "Decline all requests",
      body: "Decline all current requests and keep this slot on the board?",
      confirmLabel: "Decline all",
    });
    if (!ok) return;
    setBusyId(opp.id);
    try {
      const declined = pendingRequests(opp.requests);
      await declineAllOpportunityRequests(opp.id, "", user.id);
      declined.forEach((r) =>
        notifyAgentOnOhDecision(opp, r, "declined").catch(() => {}),
      );
      toast.success("Requests declined. Slot stays up for grabs.");
    } catch (err) {
      console.error("Decline all failed:", err);
      toast.error("Could not decline. Try again.");
    } finally {
      setBusyId("");
    }
  };

  if (queue.length === 0) return null;

  return (
    <div className="mb-6">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
        Pending Your Approval
      </div>
      <div className="space-y-3">
        {queue.map((opp) => (
          <div key={opp.id} className="border border-amber-200 bg-amber-50/40 rounded-xl p-4">
            <div className="font-semibold text-slate-900">
              {opp.propertyAddress?.split(",")[0]}
              <span className="font-normal text-slate-500 text-sm">
                {" "}· {opp.listingAgentName || "—"} ·{" "}
                {pendingRequests(opp.requests).length} request
                {pendingRequests(opp.requests).length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="mt-2 divide-y divide-slate-200">
              {pendingRequests(opp.requests).map((r) => (
                <div key={r.id} className="flex items-center gap-3 py-2">
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-slate-800">{r.agentName}</div>
                    <div className="text-xs text-slate-500">
                      {r.proposedDate} · {formatTimeRange(r.proposedStartTime, r.proposedEndTime)}
                      {r.note ? ` · "${r.note}"` : ""}
                    </div>
                  </div>
                  <button
                    onClick={() => handleApprove(opp, r)}
                    disabled={busyId === opp.id}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold px-3 py-1.5 rounded-md"
                  >
                    Approve
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() => handleDeclineAll(opp)}
              disabled={busyId === opp.id}
              className="mt-2 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-50"
            >
              ✕ Decline All &amp; Keep On Board
            </button>
          </div>
        ))}
      </div>
      {dialog}
    </div>
  );
}
